import type { ColumnDef } from "@tanstack/react-table"

import { InventoryLotStack } from "../../../components"
import { formatInventoryLocation } from "../../../utils"
import { ProductInventoryPalletLotNumberStack, ProductInventoryPalletRowActions } from "../components"
import { ProductInventoryPalletStatusStack } from "../components/ProductInventoryPalletsTab/ProductInventoryPalletStatusStack"
import { getInventoryLotKey } from "../utils"

import type { InventoryByPalletItem, Product, ProductPackagingType } from "@/graphql/codegen/graphql"
import { COST_BREAKDOWN_DECIMAL_PRECISION } from "@/screens/Procurement/CostBreakdown/constants"
import { formatNumber } from "@/utils/number"
import { getProductPackagingTypeLabel } from "@/utils/product"
import { isNil } from "@/utils/utils"

interface UseProductInventoryPalletColumnsProps {
  product: Product
  canMaintain: boolean
  onHoldPut?: (item: InventoryByPalletItem) => void
  onHoldRelease?: (item: InventoryByPalletItem) => void
  onTransfer?: (item: InventoryByPalletItem) => void
  onCombinePallets?: (item: InventoryByPalletItem) => void
  onCoaView?: (item: InventoryByPalletItem) => void
}

export const useProductInventoryPalletColumns = ({
  product,
  canMaintain,
  onHoldPut,
  onHoldRelease,
  onTransfer,
  onCombinePallets,
  onCoaView,
}: UseProductInventoryPalletColumnsProps): ColumnDef<InventoryByPalletItem>[] => {
  const uomName = product.packageUsWeightUomDetails?.name ?? ""

  return [
    {
      accessorKey: "referenceNbr",
      header: "Reference #",
      cell: ({ row }) => row.original.referenceNbr,
      enableSorting: false,
    },
    {
      accessorKey: "location",
      header: "Location",
      cell: ({ row }) => formatInventoryLocation(row.original.location),
      enableSorting: false,
    },
    {
      accessorKey: "totalUom",
      header: "Total UoM",
      cell: ({ row }) => (isNil(row.original.totalUom) ? "" : `${formatNumber(row.original.totalUom)} ${uomName}`),
      enableSorting: false,
    },
    {
      accessorKey: "laidDownCost",
      header: "Laid Down Cost",
      cell: ({ row }) =>
        isNil(row.original.laidDownCost)
          ? ""
          : `$${formatNumber(row.original.laidDownCost, COST_BREAKDOWN_DECIMAL_PRECISION, COST_BREAKDOWN_DECIMAL_PRECISION)} / ${uomName}`,
      enableSorting: false,
    },
    {
      accessorKey: "lot",
      header: "Lot #",
      cell: ({ row }) => <ProductInventoryPalletLotNumberStack lots={row.original.lots} />,
      enableSorting: false,
    },
    {
      accessorKey: "batch",
      header: "Batch #",
      cell: ({ row }) => (
        <InventoryLotStack lots={row.original.lots} getKey={getInventoryLotKey} render={(lot) => lot.batchNumber} />
      ),
      enableSorting: false,
    },
    {
      accessorKey: "lotUom",
      header: "Lot UoM",
      cell: ({ row }) => (
        <InventoryLotStack
          lots={row.original.lots}
          getKey={getInventoryLotKey}
          render={(lot) => (isNil(lot.lotUom) ? "" : `${formatNumber(lot.lotUom)} ${uomName}`)}
        />
      ),
      enableSorting: false,
    },
    {
      accessorKey: "remainingShelfLife",
      header: "Remaining Shelf Life",
      cell: ({ row }) => (
        <InventoryLotStack
          lots={row.original.lots}
          getKey={getInventoryLotKey}
          render={(lot) => (isNil(lot.remainingShelfLife) ? "" : `${lot.remainingShelfLife} %`)}
        />
      ),
      enableSorting: true,
    },
    {
      accessorKey: "pkgUom",
      header: "Pkg UoM",
      cell: ({ row }) => (
        <InventoryLotStack
          lots={row.original.lots}
          getKey={getInventoryLotKey}
          render={(lot) => (isNil(lot.packageUom) ? "" : `${formatNumber(lot.packageUom)} ${uomName}`)}
        />
      ),
      enableSorting: false,
    },
    {
      accessorKey: "packaging",
      header: "Packaging",
      cell: ({ row }) => (
        <InventoryLotStack
          lots={row.original.lots}
          getKey={getInventoryLotKey}
          render={(lot) => getProductPackagingTypeLabel(lot.packageDesc as ProductPackagingType)}
        />
      ),
      enableSorting: false,
    },
    {
      accessorKey: "inventoryStatus",
      header: "Inventory Status",
      cell: ({ row }) => <ProductInventoryPalletStatusStack lots={row.original.lots} />,
      enableSorting: false,
    },
    {
      accessorKey: "actions",
      header: "",
      cell: ({ row }) => (
        <ProductInventoryPalletRowActions
          row={row}
          canMaintain={canMaintain}
          onHoldPut={onHoldPut}
          onHoldRelease={onHoldRelease}
          onTransfer={onTransfer}
          onCombinePallets={onCombinePallets}
          onCoaView={onCoaView}
        />
      ),
      size: 50,
      enableSorting: false,
    },
  ]
}
